import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { cached } from "@/lib/server-cache";

/** Publik metadata för en guide. Själva låsta innehållet returneras aldrig här. */
export const getResourceMeta = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({
    slug: z.string().trim().max(80),
    locale: z.enum(["sv", "en", "es"]).default("sv"),
  }).parse(data))
  .handler(async ({ data }) => cached(`resource-meta:${data.locale}:${data.slug}`, 5 * 60_000, async () => {
    const { resourceBySlug, resourceIntro, resourceTitle } = await import("@/lib/resources");
    const resource = resourceBySlug(data.slug);
    if (!resource) return null;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: edited } = await supabaseAdmin
      .from("locked_resource_content")
      .select("title, description")
      .eq("resource_slug", resource.slug)
      .eq("locale", data.locale)
      .eq("status", "published")
      .maybeSingle();

    return {
      slug: resource.slug,
      title: edited?.title || resourceTitle(resource, data.locale),
      description: edited?.description || resourceIntro(resource, data.locale),
    };
  }));

/** Publikt: registrerar adressen och mejlar en personlig länk. Ingen direkt tillgång. */
export const unlockResources = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => z.object({
    email: z.string().trim().email("Fyll i en giltig e-postadress").max(255),
    name: z.string().trim().max(120).default(""),
    locale: z.enum(["sv", "en", "es"]).default("sv"),
    slug: z.string().trim().max(80).default(""),
    wantsNewsletter: z.boolean().default(false),
    company: z.string().max(200).default(""),
  }).parse(data))
  .handler(async ({ data }) => {
    if (data.company.trim() !== "") return { ok: true as const, token: "", linkSent: true as const };

    const email = data.email.trim().toLowerCase();
    const source = data.slug ? `kunskapsbank:${data.slug}` : "kunskapsbank";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("id")
      .ilike("email", email)
      .maybeSingle();

    if (existing) {
      const { error: updateError } = await supabaseAdmin
        .from("newsletter_subscribers")
        .update({
          ...(data.name ? { name: data.name } : {}),
          locale: data.locale,
          // Ikryssad ruta ger båda utskicken även för befintliga adresser.
          // En urkryssad ruta rör aldrig befintliga val.
          ...(data.wantsNewsletter ? { wants_newsletter: true, wants_blog: true } : {}),
        })
        .eq("id", existing.id);
      if (updateError) console.error("resource unlock update failed", updateError);
    } else {
      const { error } = await supabaseAdmin.from("newsletter_subscribers").insert({
        email,
        name: data.name,
        locale: data.locale,
        wants_blog: data.wantsNewsletter,
        wants_newsletter: data.wantsNewsletter,
        source,
      });
      if (error) {
        console.error("resource unlock insert failed", error);
        throw new Error("Det gick inte att skicka länken just nu.");
      }
    }

    const { sendAccessLink } = await import("@/lib/resource-access.server");
    const result = await sendAccessLink({ email, locale: data.locale, slug: data.slug });
    if (!result.ok) throw new Error(result.error);

    return { ok: true as const, token: "", linkSent: true as const };
  });

/** Publikt men låst: hämtar fördjupningsmaterialet när åtkomstnyckeln är giltig. */
export const getResourceContent = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => z.object({
    slug: z.string().trim().max(80),
    locale: z.enum(["sv", "en", "es"]).default("sv"),
    token: z.string().trim().max(200).default(""),
  }).parse(data))
  .handler(async ({ data }) => {
    if (!data.token) return { unlocked: false as const, blocks: [] };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: subscriber } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("id, status, confirmed_at")
      .eq("unsubscribe_token", data.token)
      .maybeSingle();

    // Tillgång kräver både aktiv prenumeration och en bekräftad (klickad) länk.
    if (!subscriber || subscriber.status !== "active" || !subscriber.confirmed_at) {
      return { unlocked: false as const, blocks: [] };
    }

    const { ensureLockedResourceRows } = await import("@/lib/locked-resources.server");
    await ensureLockedResourceRows();
    const { resourceBySlug } = await import("@/lib/resources");
    const meta = resourceBySlug(data.slug);
    if (!meta) return { unlocked: true as const, blocks: [] };

    const { data: edited } = await supabaseAdmin
      .from("locked_resource_content")
      .select("content")
      .eq("resource_slug", meta.slug)
      .eq("locale", data.locale)
      .eq("status", "published")
      .maybeSingle();

    return { unlocked: true as const, blocks: Array.isArray(edited?.content) ? edited.content : [] };
  });

/** Publikt: löser in den personliga länken och returnerar åtkomstnyckeln. */
export const redeemAccessLink = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ token: z.string().trim().min(10).max(200) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { consumeAccessLink } = await import("@/lib/resource-access.server");
    const result = await consumeAccessLink(data.token);
    if (!result.ok) {
      return {
        ok: false as const,
        needsResubscribe: result.needsResubscribe === true,
        error: result.error,
      };
    }
    return { ok: true as const, accessToken: result.accessToken, slug: result.slug };
  });

/** Publikt: återaktiverar prenumerationen som hör till länken och öppnar materialet. */
export const resubscribeForResources = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ token: z.string().trim().min(10).max(200) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { resubscribeViaAccessLink } = await import("@/lib/resource-access.server");
    const result = await resubscribeViaAccessLink(data.token);
    if (!result.ok) return { ok: false as const, error: result.error };
    return { ok: true as const, accessToken: result.accessToken, slug: result.slug };
  });
